/**
 * Backend AI Client - Calls the Rust backend for intent detection
 *
 * This client sends sanitized messages to the backend for Cloud AI analysis.
 * The backend handles:
 * - API key management (secure server-side)
 * - Multi-tier model selection based on complexity
 * - PII sanitization verification
 * - Intent caching
 *
 * The frontend only needs to send the message - backend handles everything else.
 */

import { graphqlMutation } from '../../database/graphqlClient';
import type { DetectedIntent } from './types';

// =============================================================================
// TYPES
// =============================================================================

export interface BackendOrchestrateInput {
  /** User message to analyze */
  message: string;
  /** Optional current view context */
  currentView?: string;
  /** Optional selected project ID */
  selectedProjectId?: string;
}

export interface BackendOrchestrateResponse {
  /** Whether orchestration succeeded */
  success: boolean;
  /** Human-readable response */
  response: string;
  /** Detected intent (if any) */
  intent: {
    category: string;
    action: string;
    confidence: number;
    mutation: string | null;
    query: string | null;
    requiredEntities: string[];
  } | null;
  /** Whether result came from cache */
  fromCache: boolean;
  /** Types of PII detected */
  piiDetected: string[];
  /** Complexity tier used (LIGHT, STANDARD, HEAVY, REASONING) */
  complexityTier: string | null;
  /** AI model used */
  modelUsed: string | null;
  /** Error message if any */
  error: string | null;
  /** The sanitized message sent to Cloud AI (for privacy verification) */
  sanitizedMessage: string | null;
}

// =============================================================================
// GRAPHQL MUTATION
// =============================================================================

const ORCHESTRATE_MESSAGE_MUTATION = `
  mutation OrchestrateMessage($input: OrchestrateMessageInput!) {
    orchestrateMessage(input: $input) {
      success
      response
      intent {
        category
        action
        confidence
        mutation
        query
        requiredEntities
      }
      fromCache
      piiDetected
      complexityTier
      modelUsed
      error
      sanitizedMessage
    }
  }
`;

// =============================================================================
// CLIENT
// =============================================================================

/**
 * Call the backend AI orchestrator
 *
 * The backend will:
 * 1. Sanitize the message (verify no PII in cloud calls)
 * 2. Classify complexity using LIGHT model
 * 3. Select appropriate model tier
 * 4. Call Cloud AI (GPT/Claude) for intent detection
 * 5. Cache result for future similar messages
 *
 * @param input - Message and optional context
 * @returns Orchestration result with detected intent
 */
export async function orchestrateMessage(
  input: BackendOrchestrateInput
): Promise<BackendOrchestrateResponse> {
  try {
    console.log('[BackendClient] Calling orchestrateMessage...');

    const result = await graphqlMutation<{
      orchestrateMessage: BackendOrchestrateResponse;
    }>(ORCHESTRATE_MESSAGE_MUTATION, {
      input: {
        message: input.message,
        currentView: input.currentView,
        selectedProjectId: input.selectedProjectId,
      },
    });

    console.log('[BackendClient] Response:', {
      success: result.orchestrateMessage.success,
      category: result.orchestrateMessage.intent?.category,
      complexityTier: result.orchestrateMessage.complexityTier,
      fromCache: result.orchestrateMessage.fromCache,
    });

    return result.orchestrateMessage;
  } catch (error) {
    console.error('[BackendClient] Error:', error);

    return {
      success: false,
      response: 'Mi dispiace, non sono riuscita a elaborare la richiesta. Riprova.',
      intent: null,
      fromCache: false,
      piiDetected: [],
      complexityTier: null,
      modelUsed: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Convert backend response to DetectedIntent format
 * Compatible with existing orchestrator types
 */
export function toDetectedIntent(response: BackendOrchestrateResponse): DetectedIntent | null {
  if (!response.intent) return null;

  return {
    category: response.intent.category as DetectedIntent['category'],
    action: response.intent.action as DetectedIntent['action'],
    confidence: response.intent.confidence,
    mutation: response.intent.mutation ?? undefined,
    query: response.intent.query ?? undefined,
    requiredEntities: response.intent.requiredEntities,
    optionalEntities: [],
  };
}

/**
 * Check if backend AI is available
 * Simply tries to connect to GraphQL endpoint
 */
export async function isBackendAIAvailable(): Promise<boolean> {
  try {
    // Try a simple query to verify connectivity
    const result = await orchestrateMessage({
      message: 'test',
      currentView: 'test',
    });
    // If we get a response (even an error), backend is reachable
    return true;
  } catch {
    return false;
  }
}
